import { memo, useEffect, useRef, useState } from 'react';
import type { DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, PALETTE, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Era } from '../../art.js';
import { GROUND, SPECKS, demoRound, feedbackTo, mix, playDemo, wave } from './garden.js';
import type { Feedback } from './garden.js';

/**
 * BIOTECHNICIAN · POLLINATE — fly the bee from a flower to one just like it.
 *
 * Flowers of a few kinds, each kind told apart by its shape and its colour, stand two by two in
 * the garden. Drag the bee from one flower to its twin (or tap one, then the other) and a trail
 * of pollen joins them; carrying it to a different kind is the mistake. Round 1 has two pairs
 * standing still but for a breath of wind, round 2 two pairs swaying a little more, round 3 three
 * pairs swaying in a breeze. (Flowers that close for a moment, their kind hidden, the old round 3,
 * are still in the rules; no round closes them since 6 Oct 2026.) The bee waits by its hive.
 */

export type Kind = 'daisy' | 'star' | 'button' | 'tulip';
export const KINDS: readonly Kind[] = ['daisy', 'star', 'button', 'tulip'];

export interface Flower {
  id: number;
  kind: Kind;
  /** Where it grows, as fractions of the board. */
  x: number;
  y: number;
  /** When it first closes, seconds into the round (a round that closes them); the others follow in turn. */
  phase: number;
}

/**
 * The rounds (easier since 6 Oct 2026, kit `easier`): two pairs, two, then three; the flowers sway a
 * little, a little more, then more. No round closes its flowers any more (the old round 3 did).
 */
export const BEE_LEVELS: readonly { pairs: number; sway: number; closes: boolean }[] = easier([
  { pairs: 2, sway: 1.5, closes: false },
  { pairs: 3, sway: 7, closes: false },
]);

export const pairsFor = (level: Level) => BEE_LEVELS[level]!.pairs;

/** The garden's nine places, three by three, as fractions of the board. */
const SLOTS: readonly { x: number; y: number }[] = [0.18, 0.5, 0.82].flatMap((y) => [0.18, 0.5, 0.82].map((x) => ({ x, y })));

/** One flower closes for this long in every cycle, in a round that closes them. */
export const CLOSE_CYCLE = 7.5;
const FOLD = 0.45;
const SHUT = 1.3;

/**
 * A round's garden: the level's number of pairs, each of a different kind, on nine places three by
 * three (a little jittered), and the hive on a place left over.
 */
export function plantGarden(rand: () => number, level: Level): { flowers: Flower[]; hive: { x: number; y: number } } {
  const pairs = pairsFor(level);
  const shuffle = <T,>(xs: readonly T[]): T[] => {
    const a = xs.slice();
    for (let i = a.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  };
  const kinds = shuffle(KINDS).slice(0, pairs);
  const slots = shuffle(SLOTS);
  const order = shuffle(Array.from({ length: pairs * 2 }, (_, i) => i));
  const flowers = shuffle(kinds.flatMap((k) => [k, k])).map((kind, i) => {
    const s = slots[i]!;
    return {
      id: i,
      kind,
      x: s.x + (rand() - 0.5) * 0.04,
      y: s.y + (rand() - 0.5) * 0.04,
      // The first to close does so 2.5 s in; the rest follow, spread over the cycle.
      phase: 2.5 + (order[i]! * CLOSE_CYCLE) / (pairs * 2),
    };
  });
  const h = slots[pairs * 2]!;
  return { flowers, hive: { x: h.x, y: h.y } };
}

/**
 * How open a flower is, 0 (a shut bud) to 1, `t` seconds into the round, for a flower that first
 * closes at `first`. Only a round set to close them does (none since 6 Oct 2026); otherwise always open.
 */
export function openness(level: Level, t: number, first: number): number {
  return BEE_LEVELS[level]!.closes ? closingOpenness(t, first) : 1;
}

/** How open a flower is in a round that closes them: shut for a moment, then open for the rest of the cycle. */
export function closingOpenness(t: number, first: number): number {
  if (t < first) return 1;
  const u = (t - first) % CLOSE_CYCLE;
  if (u < FOLD) return 1 - u / FOLD;
  if (u < FOLD + SHUT) return 0;
  if (u < 2 * FOLD + SHUT) return (u - FOLD - SHUT) / FOLD;
  return 1;
}

/** Open enough to land on. */
export const canLand = (open: number) => open >= 0.6;

/** A finger this close to a flower's middle is on it, in board units. */
export const HIT = 36;

const W = BOARD.w;
const H = BOARD.h;
/** The ✓ or ✗ of a visit shows this long. */
const VISIT_MS = 650;
/** "It is shut" shows this long after a shut flower is touched. */
const SHUT_MS = 1_200;
/** A finger that moves this far from a flower is dragging the bee. */
const DRAG = 12;

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

/** Everything the garden shows. */
export interface BeesView {
  /** The game clock, ms. */
  t: number;
  /** The flowers: across and down (units), and kind (0 daisy, 1 star, 2 button, 3 tulip). */
  f: readonly (readonly [x: number, y: number, kind: number])[];
  /** How open each is (0 shut – 1 open) in a round that closes them; null while all stay open. */
  o: readonly number[] | null;
  /** The pairs joined, as places in `f`, and how many pairs in all. */
  p: readonly (readonly [a: number, b: number])[];
  of: number;
  /** The hive, and the bee (units). */
  h: readonly [x: number, y: number];
  b: readonly [x: number, y: number];
  /** The flower whose pollen the bee carries (−1: none), and whether it is being dragged. */
  c: number;
  drag: boolean;
  /** The last flower the bee flew to: which, right (1) or not (0), and ms ago, while it shows. */
  v: readonly [i: number, ok: 0 | 1, age: number] | null;
  /** A shut flower was just touched. */
  shut: boolean;
  /** How far the flowers sway, degrees. */
  sw: number;
  /** Every pair joined. */
  won: boolean;
  fx?: Fx | undefined;
}

/** A round of the garden, driven by a finger (a guest's or the how-to's) and the game clock. */
export function beesRound(level: Level, garden: { flowers: readonly Flower[]; hive: { x: number; y: number } }, fb: Feedback) {
  const L = BEE_LEVELS[level]!;
  const { flowers, hive } = garden;
  const at = (f: { x: number; y: number }): Pt => ({ x: f.x * W, y: f.y * H });
  const done = new Set<number>();
  const joined: (readonly [number, number])[] = [];
  let carrying: number | null = null;
  /** The gesture under way: where it began, on which flower, and whether it has become a drag. */
  let gesture: { start: Pt; flower: number | null; fromBee: boolean; dragging: boolean } | null = null;
  let finger: Pt | null = null;
  let visit: { id: number; at: number; ok: boolean } | null = null;
  let shutAt = -1e9;
  let startAt: number | null = null;
  let won = false;
  const home = at(hive);
  const bee: Pt = { x: home.x + 2, y: home.y - 26 };

  const secs = (now: number) => (startAt === null ? 0 : (now - startAt) / 1000);
  /** The open, unpollinated flower under a point, nearest first. */
  const flowerAt = (p: Pt, now: number, except: number | null = null): Flower | null => {
    let best: Flower | null = null;
    let bestD = HIT;
    for (const f of flowers) {
      if (f.id === except || done.has(f.id) || !canLand(openness(level, secs(now), f.phase))) continue;
      const d = dist(at(f), p);
      if (d <= bestD) {
        best = f;
        bestD = d;
      }
    }
    return best;
  };
  const shutUnder = (p: Pt, now: number) => flowers.some((f) => !done.has(f.id) && !canLand(openness(level, secs(now), f.phase)) && dist(at(f), p) <= HIT);

  const evaluate = (from: number, to: number, now: number) => {
    const a = flowers[from]!;
    const b = flowers[to]!;
    carrying = null;
    if (a.kind === b.kind) {
      done.add(a.id);
      done.add(b.id);
      joined.push([a.id, b.id]);
      visit = { id: b.id, at: now, ok: true };
      fb.hit();
      fb.progress(joined.length / L.pairs);
      if (joined.length >= L.pairs) {
        won = true;
        fb.win();
      }
    } else {
      visit = { id: b.id, at: now, ok: false };
      fb.miss();
    }
  };

  /** Where the bee is going: under the finger, on the flower it carries from, on the flower it just visited, or home. */
  const target = (now: number): Pt => {
    if (gesture?.dragging && finger) return { x: finger.x, y: finger.y - 20 };
    const on = carrying ?? (visit && now - visit.at < VISIT_MS ? visit.id : null);
    if (on !== null) {
      const c = at(flowers[on]!);
      return { x: c.x + 8, y: c.y - 16 };
    }
    return { x: home.x + 2, y: home.y - 26 };
  };

  return {
    input: {
      down(p: Pt, now: number) {
        if (won) return;
        const f = flowerAt(p, now);
        if (!f && shutUnder(p, now)) shutAt = now;
        const beeAt = carrying !== null ? at(flowers[carrying]!) : null;
        gesture = { start: p, flower: f?.id ?? null, fromBee: !f && beeAt !== null && dist(beeAt, p) <= HIT, dragging: false };
      },
      move(p: Pt) {
        const g = gesture;
        if (!g || won) return;
        if (!g.dragging && dist(p, g.start) > DRAG && (g.flower !== null || g.fromBee)) {
          g.dragging = true;
          carrying = g.flower ?? carrying;
        }
        if (g.dragging) finger = p;
      },
      up(now: number) {
        const g = gesture;
        gesture = null;
        const p = finger;
        finger = null;
        if (!g || won) return;
        if (g.dragging) {
          const src = carrying!;
          const tgt = p ? flowerAt(p, now, src) : null;
          // Let go in the grass or on a shut flower: the bee goes back to where it took the pollen.
          if (tgt) evaluate(src, tgt.id, now);
          else if (p && shutUnder(p, now)) shutAt = now;
          return;
        }
        if (g.flower === null) return;
        if (carrying !== null && carrying !== g.flower) evaluate(carrying, g.flower, now);
        else carrying = carrying === g.flower ? null : g.flower;
      },
    },
    /** Covered mid-drag: the drag is dropped where it stood. */
    drop() {
      gesture = null;
      finger = null;
    },
    step(dt: number, now: number) {
      if (startAt === null) startAt = now;
      // The bee flies to where it is going, quickly at first, then settling; under a finger it stays under it.
      const to = target(now);
      const k = gesture?.dragging ? 1 : Math.min(1, dt * 9);
      bee.x += (to.x - bee.x) * k;
      bee.y += (to.y - bee.y) * k;
    },
    view(now: number, fx: Fx | undefined): BeesView {
      return {
        t: q(now),
        f: flowers.map((f) => [q(f.x * W), q(f.y * H), KINDS.indexOf(f.kind)] as const),
        o: L.closes ? flowers.map((f) => (done.has(f.id) ? 1 : q(openness(level, secs(now), f.phase), 2))) : null,
        p: joined.slice(),
        of: L.pairs,
        h: [q(home.x), q(home.y)],
        b: [q(bee.x), q(bee.y)],
        c: carrying ?? -1,
        drag: gesture?.dragging === true && finger !== null,
        v: visit && now - visit.at < VISIT_MS ? [visit.id, visit.ok ? 1 : 0, q(now - visit.at)] : null,
        shut: now - shutAt < SHUT_MS,
        sw: L.sway,
        won,
        fx,
      };
    },
  };
}

/** Pollen: the bee's dusting and the trails it leaves. */
const POLLEN = '#ffd84a';

/** The garden, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<BeesView>) {
  const id = useSvgId();
  const t = view.t;
  // A demo glides every number between its keys: places and states are read rounded.
  const carried = Math.round(view.c);
  const visit = view.v ? { i: Math.round(view.v[0]), ok: Math.round(view.v[1]) === 1, age: view.v[2] } : null;
  const missed = visit !== null && !visit.ok;
  const wobble = missed ? Math.sin(visit.age / 35) * 5 * Math.max(0, 1 - visit.age / 650) : 0;
  const done = new Set(view.p.flatMap(([a, b]) => [Math.round(a), Math.round(b)]));
  const pt = (i: number): Pt => ({ x: view.f[i]![0], y: view.f[i]![1] });

  const status = view.won
    ? { text: say(lang, '✓ ALL POLLINATED', '✓ ALT POLLINERT'), tone: 'ok' as const }
    : missed
      ? { text: say(lang, 'NOT THE SAME FLOWER', 'IKKE SAMME BLOMST'), tone: 'bad' as const }
      : view.shut
        ? { text: say(lang, 'IT IS SHUT — WAIT', 'DEN ER LUKKET – VENT'), tone: 'warn' as const }
        : carried >= 0
          ? { text: say(lang, 'NOW TO ONE JUST LIKE IT', 'NÅ TIL EN SOM ER LIK') }
          : { text: say(lang, 'PAIRS', 'PAR'), count: [view.p.length, view.of] as const, hint: say(lang, 'DRAG TO THE SAME FLOWER', 'DRA TIL SAMME BLOMST') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <radialGradient id={`${id}ground`} cx=".5" cy=".35" r=".85">
          <stop offset="0" stopColor={GROUND[era].soilTop} stopOpacity=".55" />
          <stop offset="1" stopColor={GROUND[era].soilBottom} stopOpacity=".85" />
        </radialGradient>
        <radialGradient id={`${id}gold`}>
          <stop offset=".45" stopColor={POLLEN} stopOpacity=".5" />
          <stop offset="1" stopColor={POLLEN} stopOpacity="0" />
        </radialGradient>
        <FlowerDefs id={id} era={era} />
      </defs>
      <MeadowArt era={era} id={id} />

      {/* the hive */}
      <g transform={`translate(${view.h[0]} ${view.h[1] + 6})`}>
        <HiveArt />
      </g>

      {/* pollen trails: the pairs already joined, then the one being flown */}
      {view.p.map(([a, b]) => {
        const A = pt(Math.round(a));
        const B = pt(Math.round(b));
        return (
          <path
            key={`${a}-${b}`}
            d={`M${A.x} ${A.y} Q ${(A.x + B.x) / 2} ${(A.y + B.y) / 2 + 28} ${B.x} ${B.y}`}
            fill="none"
            stroke={POLLEN}
            strokeOpacity=".5"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="0.1 7"
          />
        );
      })}
      {view.drag && carried >= 0 && (
        <path
          d={`M${pt(carried).x} ${pt(carried).y} Q ${(pt(carried).x + view.b[0]) / 2} ${(pt(carried).y + view.b[1] + 20) / 2 + 14} ${view.b[0]} ${view.b[1] + 20}`}
          fill="none"
          stroke={POLLEN}
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeDasharray="0.1 6.5"
        />
      )}

      {view.f.map(([x, y, kind], i) => {
        const isDone = done.has(i);
        const open = isDone || !view.o ? 1 : (view.o[i] ?? 1);
        const rot = Math.sin((t / 1000) * 1.3 + i * 2.1) * view.sw;
        const shake = missed && visit.i === i ? wobble : 0;
        return (
          <g key={i} transform={`translate(${(x + shake + rot * 0.25).toFixed(1)} ${y})`}>
            <ellipse cx="2" cy="24" rx="20" ry="5" fill="#000" opacity=".3" />
            {(isDone || carried === i) && <circle r="36" fill={`url(#${id}gold)`} />}
            <g transform={`rotate(${rot.toFixed(2)})`}>
              <FlowerArt kind={KINDS[Math.round(kind)] ?? 'daisy'} open={open} id={id} era={era} />
            </g>
            {isDone &&
              [0, 1, 2, 3, 4].map((k) => <circle key={k} cx={Math.cos(k * 1.26 + i) * 27} cy={Math.sin(k * 1.26 + i) * 27} r="1.6" fill={POLLEN} opacity=".85" />)}
          </g>
        );
      })}

      {/* the bee: it bobs while it waits, and its wings beat */}
      <g transform={`translate(${(view.b[0] + wobble).toFixed(1)} ${(view.b[1] - (view.drag ? 0 : 3 * wave(t, 1_600))).toFixed(1)})`}>
        <Bee carrying={carried >= 0 || view.drag} flap={1 - 0.65 * wave(t, 180)} />
      </g>
    </BoardFrame>
  );
}

/** The ground the flowers grow in: grass and soil in the era's colours. */
function Meadow({ era, id }: { era: Era; id: string }) {
  const g = GROUND[era];
  return (
    <g>
      <rect x="0" y="0" width={W} height={H} fill={`url(#${id}ground)`} />
      {era === 'neon' && (
        // A rooftop garden: planter boxes edged in light.
        <g fill="none" stroke={g.line} strokeOpacity=".16" strokeWidth="1">
          <rect x={W * 0.03} y={H * 0.04} width={W * 0.94} height={H * 0.28} rx="6" />
          <rect x={W * 0.03} y={H * 0.36} width={W * 0.94} height={H * 0.28} rx="6" />
          <rect x={W * 0.03} y={H * 0.68} width={W * 0.94} height={H * 0.28} rx="6" />
        </g>
      )}
      {era === 'debris' && (
        <g fill="none" stroke={g.line} strokeOpacity=".14" strokeWidth="1">
          <path d={`M0 ${H * 0.3} L${W * 0.2} ${H * 0.34} L${W * 0.3} ${H * 0.28}`} />
          <path d={`M${W} ${H * 0.7} L${W * 0.8} ${H * 0.66} L${W * 0.72} ${H * 0.74}`} />
        </g>
      )}
      {era === 'dust' &&
        [0.25, 0.55, 0.85].map((y) => (
          <path key={y} d={`M0 ${H * y} Q ${W * 0.25} ${H * y - 8} ${W * 0.5} ${H * y} T ${W} ${H * y}`} fill="none" stroke={g.line} strokeOpacity=".1" strokeWidth="1.2" />
        ))}
      {/* tufts of grass */}
      {SPECKS.map((p, i) => {
        const x = p.x * W;
        const y = p.y * H;
        return (
          <g key={i} stroke={i % 3 === 0 ? g.leafHi : g.leaf} strokeOpacity={era === 'dust' ? 0.3 : 0.4} strokeWidth="1.3" strokeLinecap="round" fill="none">
            <path d={`M${x} ${y} q -1 -6 -4 -9`} />
            <path d={`M${x + 1.5} ${y} q 0 -7 1 -11`} />
            <path d={`M${x + 3} ${y} q 1 -5 4 -8`} />
          </g>
        );
      })}
    </g>
  );
}

/** A straw skep, where the bee waits. */
function Hive() {
  return (
    <g>
      <ellipse cx="0" cy="17" rx="19" ry="4" fill="#000" opacity=".35" />
      {[0, 1, 2, 3].map((i) => (
        <ellipse key={i} cx="0" cy={12 - i * 7} rx={17 - i * 3.4} ry="5" fill={i % 2 ? '#d9a441' : '#c68f2f'} stroke="#7a5418" strokeWidth=".8" />
      ))}
      <ellipse cx="0" cy="-15" rx="4.5" ry="3" fill="#e4b458" stroke="#7a5418" strokeWidth=".8" />
      <path d="M-5 16 Q 0 8 5 16 Z" fill="#2a1a08" />
      <path d="M-12 -2 Q -6 -6 2 -5" fill="none" stroke="#fff3c4" strokeOpacity=".45" strokeWidth="1.2" strokeLinecap="round" />
    </g>
  );
}

/** The bee, about 34 units across; its wings `flap` (1 open, smaller folded), pollen on its legs while it carries some. */
function Bee({ carrying, flap }: { carrying: boolean; flap: number }) {
  return (
    <g>
      <ellipse cx="1" cy="15" rx="9" ry="2.6" fill="#000" opacity=".3" />
      {/* wings, beating from where they join the body */}
      <g transform={`translate(0 -2) scale(1 ${flap.toFixed(2)}) translate(0 2)`}>
        <ellipse cx="-4" cy="-9" rx="5" ry="8" transform="rotate(-24 -4 -9)" fill="#e8f6ff" fillOpacity=".7" stroke="#ffffff" strokeOpacity=".8" strokeWidth=".6" />
        <ellipse cx="4" cy="-9" rx="5" ry="8" transform="rotate(24 4 -9)" fill="#e8f6ff" fillOpacity=".7" stroke="#ffffff" strokeOpacity=".8" strokeWidth=".6" />
      </g>
      {/* body */}
      <ellipse cx="0" cy="0" rx="11" ry="7.5" fill="#ffc526" stroke="#5a3a00" strokeWidth=".8" />
      <path d="M-3.5 -7 Q -1.5 0 -3.5 7" fill="none" stroke="#231a0c" strokeWidth="3" />
      <path d="M3 -7.2 Q 5 0 3 7.2" fill="none" stroke="#231a0c" strokeWidth="3" />
      <path d="M-11 0 L-14.5 0" stroke="#231a0c" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10.5" cy="-0.5" r="5" fill="#2b2112" />
      <circle cx="12.4" cy="-1.8" r="1.3" fill="#ffffff" />
      <path d="M12 -4.6 Q 14 -9 16.5 -9.6 M10 -5 Q 10.8 -9.6 13 -10.8" fill="none" stroke="#2b2112" strokeWidth=".9" strokeLinecap="round" />
      <ellipse cx="-2" cy="-4" rx="5" ry="1.6" fill="#fff6c8" opacity=".55" />
      {carrying && (
        <g fill={POLLEN}>
          <circle cx="-5" cy="8" r="2.2" />
          <circle cx="2" cy="8.6" r="2.4" />
          <circle cx="-1.5" cy="10.4" r="1.4" />
        </g>
      )}
    </g>
  );
}

/** Each kind's colour, from the kit's six told-apart colours; the shape tells them apart too. */
const KIND_COLOUR: Record<Kind, string> = { daisy: PALETTE[0], star: PALETTE[4], button: PALETTE[3], tulip: PALETTE[1] };

/** A kind's petals (light to deep, and their edge) and its heart, all from its one colour. */
const LOOKS = Object.fromEntries(
  KINDS.map((k) => {
    const c = KIND_COLOUR[k];
    return [k, { hi: mix(c, INK.hi, 0.35), lo: c, edge: mix(c, INK.onFill, 0.5), heart: mix(c, INK.hi, 0.85), heartLo: mix(c, INK.onFill, 0.25) }];
  }),
) as Record<Kind, { hi: string; lo: string; edge: string; heart: string; heartLo: string }>;
const look = (k: Kind) => LOOKS[k];

function FlowerDefs({ id, era }: { id: string; era: Era }) {
  const g = GROUND[era];
  return (
    <>
      {KINDS.map((k) => (
        <radialGradient key={k} id={`${id}${k}`} cx=".5" cy=".5" r=".6">
          <stop offset="0" stopColor={look(k).hi} />
          <stop offset="1" stopColor={look(k).lo} />
        </radialGradient>
      ))}
      {KINDS.map((k) => (
        <radialGradient key={`h${k}`} id={`${id}h${k}`} cx=".4" cy=".35" r=".7">
          <stop offset="0" stopColor={look(k).heart} />
          <stop offset="1" stopColor={look(k).heartLo} />
        </radialGradient>
      ))}
      <linearGradient id={`${id}bud`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={g.leafHi} />
        <stop offset="1" stopColor={g.stemDark} />
      </linearGradient>
    </>
  );
}

const STAR_D = (() => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? 25 : 10.5;
    const a = (i * Math.PI) / 5 - Math.PI / 2;
    pts.push(`${(Math.cos(a) * r).toFixed(2)} ${(Math.sin(a) * r).toFixed(2)}`);
  }
  return `M${pts.join(' L')} Z`;
})();

/**
 * One flower, about 56 units across, drawn round its middle, on the era's two leaves. `open` below
 * 1 folds the petals in under a green bud; shut, its kind can't be seen.
 */
export function FlowerArt({ kind, open, id, era }: { kind: Kind; open: number; id: string; era: Era }) {
  const p = look(kind);
  const g = GROUND[era];
  const s = 0.28 + 0.72 * open;
  const fill = `url(#${id}${kind})`;
  const heart = `url(#${id}h${kind})`;
  return (
    <g>
      {/* two leaves under every flower */}
      <path d="M0 6 C 10 4, 22 10, 28 18 C 18 20, 8 16, 0 6 Z" fill={g.stem} stroke={g.stemDark} strokeWidth=".7" />
      <path d="M0 6 C -9 6, -20 13, -24 21 C -14 21, -6 16, 0 6 Z" fill={g.leaf} stroke={g.stemDark} strokeWidth=".7" />
      <g transform={`scale(${s.toFixed(3)})`}>
        {kind === 'daisy' && (
          <>
            {Array.from({ length: 14 }, (_, i) => (
              <ellipse key={i} cx="0" cy="-15" rx="4" ry="11" transform={`rotate(${(i * 360) / 14})`} fill={fill} stroke={p.edge} strokeWidth=".6" />
            ))}
            <circle r="8.5" fill={heart} stroke={p.heartLo} strokeWidth=".6" />
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <circle key={i} cx={Math.cos(i * 1.05) * 4.4} cy={Math.sin(i * 1.05) * 4.4} r=".9" fill={p.edge} opacity=".7" />
            ))}
          </>
        )}
        {kind === 'star' && (
          <>
            <path d={STAR_D} fill={fill} stroke={p.edge} strokeWidth="1" strokeLinejoin="round" />
            {[0, 1, 2, 3, 4].map((i) => {
              const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
              return <line key={i} x1="0" y1="0" x2={Math.cos(a) * 19} y2={Math.sin(a) * 19} stroke={INK.hi} strokeOpacity=".45" strokeWidth="1" />;
            })}
            <circle r="5.5" fill={heart} />
            {[0, 1, 2, 3, 4].map((i) => {
              const a = (i * 2 * Math.PI) / 5 - Math.PI / 2 + 0.63;
              return <circle key={i} cx={Math.cos(a) * 8} cy={Math.sin(a) * 8} r="1.3" fill={p.heart} />;
            })}
          </>
        )}
        {kind === 'button' && (
          <>
            {[0, 1, 2, 3, 4].map((i) => {
              const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
              return (
                <g key={i}>
                  <circle cx={Math.cos(a) * 12} cy={Math.sin(a) * 12} r="11.5" fill={fill} stroke={p.edge} strokeWidth=".8" />
                  <ellipse cx={Math.cos(a) * 14 - 2} cy={Math.sin(a) * 14 - 3} rx="3.4" ry="2" fill={INK.hi} opacity=".45" />
                </g>
              );
            })}
            <circle r="6.5" fill={heart} stroke={p.heartLo} strokeWidth=".6" />
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <circle key={i} cx={Math.cos(i * 0.785) * 8.6} cy={Math.sin(i * 0.785) * 8.6} r="1" fill={p.hi} />
            ))}
          </>
        )}
        {kind === 'tulip' && (
          <>
            <path d="M0 26 L0 8" stroke={g.stemDark} strokeWidth="3" />
            <path d="M-15 -10 L-8 -22 L-2 -12 L4 -24 L9 -12 L16 -21 L15 2 Q 14 14 0 15 Q -14 14 -15 2 Z" fill={fill} stroke={p.edge} strokeWidth="1" strokeLinejoin="round" />
            <path d="M-2 -12 Q 0 2 0 14 M9 -12 Q 7 0 5 13" fill="none" stroke={p.edge} strokeOpacity=".6" strokeWidth=".9" />
            <path d="M-11 -6 Q -10 4 -5 10" fill="none" stroke={INK.hi} strokeOpacity=".5" strokeWidth="2" strokeLinecap="round" />
          </>
        )}
      </g>
      {open < 0.999 && (
        // The sepals folding up over it.
        <g opacity={(1 - open).toFixed(3)}>
          <path d="M0 -14 C 9 -12, 12 2, 0 12 C -12 2, -9 -12, 0 -14 Z" fill={`url(#${id}bud)`} stroke={g.stemDark} strokeWidth=".8" />
          <path d="M0 -14 C 4 -6, 4 4, 0 12" fill="none" stroke={g.stemDark} strokeWidth=".7" opacity=".6" />
        </g>
      )}
    </g>
  );
}

// The parts that stay the same from frame to frame are drawn once, not sixty times a second.
const MeadowArt = memo(Meadow);
const HiveArt = memo(Hive);

function Board(props: RoundProps) {
  const { level, lang } = props;
  const fb = useRef<Feedback | null>(null);
  const [game] = useState(() => beesRound(level, plantGarden(seeded(Date.now() + level * 7919), level), feedbackTo(fb)));
  const round = useRound(props, (dt, now) => game.step(dt, now));
  fb.current = round;

  // Covered mid-drag: the drag is dropped where it stood.
  useEffect(() => {
    if (props.paused) game.drop();
  }, [props.paused, game]);

  return (
    <View
      view={round.show(game.view(round.now, round.fx))}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Drag the bee between matching flowers', 'Dra bien mellom like blomster'),
        down: (p) => round.live && game.input.down(p, round.time()),
        move: (p) => round.live && game.input.move(p),
        up: () => (round.live ? game.input.up(round.time()) : game.drop()),
      }}
    />
  );
}

/** The how-to's garden: two pairs, laid by hand, the hive at the top right. */
const DEMO_GARDEN: { flowers: readonly Flower[]; hive: { x: number; y: number } } = {
  flowers: [
    { id: 0, kind: 'star', x: 0.18, y: 0.5, phase: 0 },
    { id: 1, kind: 'daisy', x: 0.5, y: 0.2, phase: 0 },
    { id: 2, kind: 'star', x: 0.82, y: 0.52, phase: 0 },
    { id: 3, kind: 'daisy', x: 0.5, y: 0.82, phase: 0 },
  ],
  hive: { x: 0.82, y: 0.18 },
};

/**
 * The how-to, flown by the rules themselves: the finger touches a star, drags the bee across to
 * the other star and lets go — a trail joins them — then taps one daisy and then the other.
 */
function demoPollinate(): DemoScript<BeesView> {
  const ms = 7_000;
  const finger: FingerKey[] = [
    { at: 0, x: 290, y: 300, hide: true },
    { at: 400, x: 290, y: 300, hide: false },
    { at: 1_000, x: 58, y: 164 },
    { at: 1_200, x: 58, y: 164, down: true },
    { at: 1_450, x: 100, y: 178, ease: 'linear' },
    { at: 2_050, x: 168, y: 192, ease: 'linear' },
    { at: 2_650, x: 262, y: 170, ease: 'linear' },
    { at: 2_750, x: 262, y: 170, down: false },
    { at: 3_400, x: 160, y: 68 },
    { at: 3_600, x: 160, y: 68, down: true },
    { at: 3_720, x: 160, y: 68, down: false },
    { at: 4_400, x: 160, y: 264 },
    { at: 4_600, x: 160, y: 264, down: true },
    { at: 4_720, x: 160, y: 264, down: false },
    { at: 5_500, x: 290, y: 300, hide: true },
  ];
  const round = demoRound();
  const game = beesRound(0, DEMO_GARDEN, round);
  const keys = playDemo(
    ms,
    finger,
    round,
    { input: game.input, step: (dt, now) => game.step(dt, now), view: (now) => game.view(now, round.fx()) },
    ({ t: _t, b: _b, v, fx: _fx, ...parts }) => ({ ...parts, v: v ? [v[0], v[1]] : null }),
  );
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'TOUCH A FLOWER', no: 'TRYKK PÅ EN BLOMST' },
      { at: 1_350, en: 'DRAG THE BEE TO ONE LIKE IT', no: 'DRA BIEN TIL EN SOM ER LIK' },
      { at: 3_150, en: 'OR TAP ONE, THEN THE OTHER', no: 'ELLER TRYKK PÅ BEGGE' },
    ],
  };
}

export const game = thirtyGame<BeesView>({
  Board,
  View,
  demo: demoPollinate(),
  words: {
    en: {
      instruction: 'Drag the bee from a flower to one just like it. A different flower is a mistake.',
      howMove: 'Touch a flower and drag the bee to a flower of the same kind. Let go on it.',
    },
    no: {
      instruction: 'Dra bien fra en blomst til en helt lik. En annen blomst er en feil.',
      howMove: 'Trykk på en blomst og dra bien til en blomst av samme slag. Slipp på den.',
    },
  },
});
